import { MenuItem, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

import type { InventoryLocationSummary } from '../../model/types';

export interface LocationSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  locations: InventoryLocationSummary[];
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  /** When set, renders a leading "all locations" option with this label (for filter bars). */
  allOptionLabel?: string;
  /** Omits one location from the list (e.g. the Transfer dialog excludes the chosen source). */
  excludeId?: string;
  size?: TextFieldProps['size'];
  fullWidth?: boolean;
  sx?: TextFieldProps['sx'];
}

/**
 * Shared location picker — a small `TextField select` used by the Stock filter bar and every
 * dialog that needs a location (Receive/Adjust/Transfer/Policy). Locations are enriched with
 * their human-readable name (see `lib/useLocationLookup.ts`); the code is shown alongside the
 * name so operators can disambiguate near-duplicate names.
 */
export function LocationSelect({
  label,
  value,
  onChange,
  locations,
  disabled,
  required,
  error,
  helperText,
  allOptionLabel,
  excludeId,
  size = 'small',
  fullWidth,
  sx,
}: LocationSelectProps) {
  const options = excludeId ? locations.filter((l) => l.id !== excludeId) : locations;

  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      required={required}
      error={error}
      helperText={helperText}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
    >
      {allOptionLabel ? <MenuItem value="">{allOptionLabel}</MenuItem> : null}
      {options.map((location) => (
        <MenuItem key={location.id} value={location.id}>
          {location.name} ({location.code})
        </MenuItem>
      ))}
    </TextField>
  );
}
